import { CustomBanner } from '@/components';
import { AccountPlan } from '@/constants/enum';
import { calcPercent, getQuotaPerMonth, handleSendMoreShipmentMessage } from '@/helpers';
import { usePlanDisplayName } from '@/hooks/usePlanDisplayName';
import { useUpgradeTracking } from '@/hooks/useUpgradeTracking';
import { UNLIMITED_QUOTA } from '@/pages/Plan/config';
import { useGetUserPlanQuery } from '@/redux/api/api.caller';
import dashboardPageSlice, { haveClosedOverQuotaBannerSelector } from '@/redux/features/dashboard.slice';
import { accountSelector } from '@/redux/features/plan.slice';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressPercent } from '../../config';
import UsageQuotaBanner from './UsageQuotaBanner';

const OverQuotaBanner = () => {
  const { navigateToPlan } = useUpgradeTracking();
  const { currentPlanName } = usePlanDisplayName();
  const dispatch = useDispatch();
  const haveClosedBanner = useSelector(haveClosedOverQuotaBannerSelector);
  const { plan } = useSelector(accountSelector);
  const { data: userPlanData } = useGetUserPlanQuery({});

  const progressInfo = useMemo(() => {
    if (userPlanData && userPlanData.data) {
      const { credit } = userPlanData.data;
      const totalQuota = getQuotaPerMonth(userPlanData.data);
      const isUnlimitedQuota = totalQuota === UNLIMITED_QUOTA;
      return {
        percent: isUnlimitedQuota ? 0 : calcPercent(credit, Number(totalQuota)),
        credit,
        totalQuota,
        isUnlimitedQuota,
      };
    }
    return null;
  }, [userPlanData]);

  const handleCloseBanner = () => {
    dispatch(dashboardPageSlice.actions.handleCloseOverQuotaBanner(true));
  };

  const handleUpgradeNow = () => {
    navigateToPlan({
      trigger_type: 'banner',
      trigger_element: 'Upgrade Now',
      feature: 'quota_limit',
    });
  };

  useEffect(() => {
    if (progressInfo && progressInfo.percent < ProgressPercent.Warning && haveClosedBanner) {
      dispatch(dashboardPageSlice.actions.handleCloseOverQuotaBanner(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressInfo]);

  if (userPlanData?.data?.billingSummary?.usageBillingActive && progressInfo) {
    return <UsageQuotaBanner billingSummary={userPlanData.data.billingSummary} totalQuota={progressInfo.totalQuota} />;
  }

  return progressInfo && !progressInfo.isUnlimitedQuota && progressInfo.percent >= ProgressPercent.Warning ? (
    (() => {
      const { credit, percent, totalQuota } = progressInfo;
      const isNotCritical = percent < ProgressPercent.Critical;

      return (
        <CustomBanner
          maxHeight="15rem"
          isClose={isNotCritical && haveClosedBanner}
          tone={isNotCritical ? 'warning' : 'critical'}
          title={
            isNotCritical
              ? "Attention: You've consumed 80% of your shipment quota in current period"
              : `Exceeded shipment quota in current period of your ${currentPlanName} plan`
          }
          action={{
            content: plan === AccountPlan.Enterprise ? 'Discuss for more shipments volumn' : 'Upgrade Now',
            onAction: plan === AccountPlan.Enterprise ? handleSendMoreShipmentMessage : handleUpgradeNow,
          }}
          onDismiss={isNotCritical ? handleCloseBanner : undefined}
        >
          {isNotCritical
            ? `Your shipment quota has reached 80% of your plan's limit of ${totalQuota} shipments, currently at ${credit} shipments in current period. To ensure uninterrupted tracking and greater flexibility, please considering an upgrade to your plan.`
            : `Your shipment quota has exceeded your plan's limit of ${totalQuota} shipments, hitting ${credit} shipments in current period. Upgrade to a higher plan now for uninterrupted tracking and more shipment flexibility.`}
        </CustomBanner>
      );
    })()
  ) : (
    <></>
  );
};

export default OverQuotaBanner;
